/**
 * Generates a CSV file containing only the header row (column keys) from
 * the loaded template and triggers a browser download.
 *
 * The generated CSV follows RFC 4180:
 * - Fields containing commas, double-quotes, or newlines are quoted.
 * - Double-quotes within fields are escaped as `""`.
 * - Lines terminate with CRLF.
 * - A UTF-8 BOM is prepended so Excel opens the file with correct encoding.
 */

import type { NormalizedTemplate } from '../types/template';

/**
 * Escapes a CSV field per RFC 4180.
 * If the value contains a comma, double-quote, or newline, it is wrapped in
 * double-quotes with internal quotes escaped.
 */
export function escapeCsvField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Builds the CSV header string from a normalized template's columns.
 * Returns a single-line CSV (header row + CRLF).
 */
export function buildTemplateCsvContent(template: NormalizedTemplate): string {
  const headers = template.columns.map((col) => escapeCsvField(col.key));
  return headers.join(',') + '\r\n';
}

/**
 * Triggers a browser download of the template CSV file.
 *
 * @param template - The normalized template containing column definitions.
 * @param filename - Optional override for the download filename.
 *   Defaults to `${template.importerConfig.title}.csv`.
 */
export function downloadTemplateCsv(
  template: NormalizedTemplate,
  filename?: string,
): void {
  const csv = buildTemplateCsvContent(template);
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download =
    filename ?? `${template.importerConfig.title || 'template'}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
